import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ReactSwitch } from '@storybook';
import { RecipientForm } from 'views/edit-recipient/components';
import { RecipientLocalState } from 'views/edit-recipient/stores';
import type { IRecipient, ISignedDocInbox } from 'views/signed-doc-inbox';
import { userCapTableState } from '../store';
import './sign-access-code.scss';

export const SignAccessCode: FC = () => {
	const userCapTableData = useRecoilValue(userCapTableState);
	const [localRecipients, setLocalRecipients] = useRecoilState(RecipientLocalState);
	const [requireAccessCodeForAll, setRequireAccessCodeForAll] = useState(false);

	const generateAccessCode = useCallback(() => {
		const specials = '!@#$%^&*()_+-={}[]:;<>,.?/~';
		const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
		const digits = '0123456789';
		const allNonSpecials = letters + digits;

		const minLen = 8;
		const maxLen = 12;
		const len = Math.floor(Math.random() * (maxLen - minLen + 1)) + minLen;

		const code = [
			letters[Math.floor(Math.random() * letters.length)],
			digits[Math.floor(Math.random() * digits.length)],
			specials[Math.floor(Math.random() * specials.length)],
		];

		while (code.length < len) {
			code.push(allNonSpecials[Math.floor(Math.random() * allNonSpecials.length)]);
		}

		// Shuffle the characters
		for (let i = code.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[code[i], code[j]] = [code[j], code[i]];
		}

		return code.join('');
	}, []);

	// Generate recipients from captable data
	const recipients = useMemo(() => {
		const { rows = [], headers = [] } = userCapTableData || {};
		const emailIndex = headers.findIndex((h: string) => /email/gi.test(h));
		const firstNameIndex = headers.findIndex((h: string) => /first.*name/gi.test(h));
		const lastNameIndex = headers.findIndex((h: string) => /last.*name/gi.test(h));

		const colors = ['#8B45F5', '#33B87A', '#FF6B6B', '#4ECDC4', '#FFE66D'];

		return rows.map((row: any[], index: number) => ({
			_id: `recipient-${index}`,
			fullName: `${row[firstNameIndex] || ''} ${row[lastNameIndex] || ''}`.trim() || `Signer ${index + 1}`,
			email: row[emailIndex] || '',
			colorCode: colors[index % colors.length],
			status: 'pending',
			action: 'sign',
			recipientType: 'signer' as const,
			userId: '',
			sentDateTime: new Date(),
			signedDateTime: new Date(),
			biometric: {
				_id: '',
				userId: '',
				business: '',
				createdAt: '',
				updatedAt: '',
				__v: 0,
			},
			isAccessCodeEnabled: false,
			accessCode: '',
		}));
	}, [userCapTableData]);

	// Sync RecipientLocalState with cap table changes
	useEffect(() => {
		if (recipients.length === 0) return;

		// Check if cap table data has changed
		const hasChanges = 
			localRecipients.length !== recipients.length ||
			recipients.some((newRecipient: IRecipient, index: number) => {
				const existingRecipient = localRecipients[index];
				return !existingRecipient || 
					existingRecipient.email !== newRecipient.email ||
					existingRecipient.fullName !== newRecipient.fullName;
			});

		if (hasChanges) {
			// Create a map of existing recipients by email to preserve access code settings
			const existingRecipientsMap = new Map(
				localRecipients.map(r => [r.email, r])
			);

			// Merge new recipients with existing access code settings
			const mergedRecipients = recipients.map((newRecipient: IRecipient) => {
				const existing = existingRecipientsMap.get(newRecipient.email);
				
				if (existing) {
					// Preserve access code settings for existing recipients
					return {
						...newRecipient,
						isAccessCodeEnabled: existing.isAccessCodeEnabled,
						accessCode: existing.accessCode,
					};
				}
				
				// New recipient - use default values
				return newRecipient;
			});

			setLocalRecipients(mergedRecipients);
		}
	}, [recipients, localRecipients, setLocalRecipients]);

	// Monitor changes in individual recipient access codes to update global toggle
	useEffect(() => {
		if (localRecipients.length > 0) {
			const allEnabled = localRecipients.every(r => r.isAccessCodeEnabled);
			setRequireAccessCodeForAll(allEnabled);
		}
	}, [localRecipients]);

	// Create mock inbox object for RecipientForm
	const mockInbox = useMemo((): ISignedDocInbox => ({
		_id: 'mock-inbox-id',
		recipients: localRecipients.length > 0 ? localRecipients : recipients,
		sender: {
			_id: '',
			userId: '',
			name: '',
			email: '',
			fullName: '',
			biometric: {},
		},
		createdAt: new Date(),
		updatedAt: new Date(),
		documents: [],
		status: 'created',
		signOrder: false,
		name: 'Access Code Configuration',
		notary: false,
		purpose: 'signAgreement',
		isLoaded: true,
		origin: 'basic',
		authType: 'facial',
	}), [localRecipients, recipients]);

	const handleToggleAccessCodeForAll = useCallback(
		(checked: boolean) => {
			setRequireAccessCodeForAll(checked);

			// Update all recipients when global toggle changes
			setLocalRecipients(prev =>
				prev.map(recipient => {
					if (checked) {
						// Enable access code for all recipients
						return {
							...recipient,
							isAccessCodeEnabled: true,
							accessCode: recipient.accessCode || generateAccessCode(),
						};
					} else {
						// Disable access code for all recipients
						return {
							...recipient,
							isAccessCodeEnabled: false,
							accessCode: '',
						};
					}
				})
			);
		},
		[setLocalRecipients, generateAccessCode]
	);

	return (
		<div className="sign-access-code">
			<div className="sign-access-code__main">
				<div className="sign-access-code__sidebar">
					<div className="sign-access-code__recipients">
						{/* Recipients Header */}
						<div className="sign-access-code__recipients-header">
							<div className="sign-access-code__recipients-title">
								Recipients
							</div>
						</div>

						{/* Global Access Code Toggle */}
						<div className="sign-access-code__global-toggle">
							<div className="sign-access-code__toggle-container">
								<div className="sign-access-code__toggle-header">
									<div className="sign-access-code__toggle-text">
										<span className="sign-access-code__toggle-title">
											Require Access Code for All Recipients
										</span>
									</div>
									<ReactSwitch
										checked={requireAccessCodeForAll}
										handleChange={handleToggleAccessCodeForAll}
										id="require-access-code-all"
										onColor="#33b87a"
									/>
								</div>
								<div className="sign-access-code__toggle-description">
									<p>
										Enable this to require a separate, unique access code for every recipient below.
									</p>
								</div>
							</div>
						</div>

						{/* Signers List */}
						<div className="sign-access-code__recipients-list">
							{(localRecipients.length > 0 ? localRecipients : recipients).map((recipient: IRecipient, index: number) => (
								<RecipientForm
									key={recipient._id}
									recipient={recipient}
									index={index}
									inbox={mockInbox}
									type="session"
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

